import type { AppAction } from './types/ActionTypes'
import { SET_OPTIMIZELY_EXPERIMENTS, SET_OPTIMIZELY_FEATURE_FLAGS, SET_RESOLVED_TRACKING_ID } from './actionConstants'
import { rebucketOptimizely } from 'api/optimizely'
import { logNewRelic } from 'services/newRelic'
import { pickKeys } from 'lib/object/objectUtils'

/**
 * Set a single Optimizely experiment (backward compatible)
 *
 * Note: This now uses the bulk update action internally for simplicity.
 * The bulk action can handle single updates just as well.
 */
export function setOptimizelyExperiment(experimentId: string, experiment: App.OptimizelyExperimentDetails, force?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (!force && state.optimizely.optimizelyExperiments[experimentId]?.triggered) {
      return // Already triggered, don't dispatch again
    }

    // Use bulk update action for consistency
    dispatch({
      type: SET_OPTIMIZELY_EXPERIMENTS,
      experiments: {
        [experimentId]: {
          ...experiment,
          triggered: !force ? true : experiment.triggered,
        },
      },
    })
  }
}

/**
 * Re-bucket Optimizely experiments after user login
 *
 * Calls the server-side re-bucketing API to get updated experiment
 * decisions based on the authenticated user's stitched seed ID.
 *
 * This action:
 * 1. Calls server endpoint (context derived from request on server)
 * 2. Receives updated experiment and feature flag decisions
 * 3. Updates Redux state with new bucketing
 * 4. Tracks re-bucketing event for analytics
 *
 * Fails silently if API call fails - user continues with existing bucketing
 */
export function rebucketOptimizelyExperiments(): AppAction {
  return async(dispatch, getState) => {
    try {
      const previousResolvedId = getState().auth.resolvedTrackingId

      // Call server-side re-bucketing endpoint
      const response = await rebucketOptimizely()

      if (response) {
        const { optimizelyExperiments, optimizelyFeatureFlags, resolvedTrackingId } = response

        // Use fresh state for comparison — state may have changed during the async API call
        // (e.g., experiments triggered while the rebucket request was in-flight)
        const freshState = getState()
        const previousExperiments = freshState.optimizely.optimizelyExperiments

        // Find which experiments changed variation
        const changedExperimentKeys = Object.keys(optimizelyExperiments).filter(
          key => optimizelyExperiments[key]?.variationKey !== previousExperiments[key]?.variationKey,
        )

        // For changed experiments, reset `triggered` to false so useOptimizelyExperiment
        // can re-fire the Snowplow activation event for the new variation
        const changedExperiments = pickKeys(changedExperimentKeys)(optimizelyExperiments)
        const experimentsWithResetTrigger: Record<string, App.OptimizelyExperimentDetails | undefined> = Object.fromEntries(
          Object.entries(changedExperiments).map(([key, experiment]) => [
            key,
            experiment ? { ...experiment, triggered: false } : undefined,
          ]),
        )

        // Update Redux state with new bucketing decisions
        dispatch({
          type: SET_OPTIMIZELY_EXPERIMENTS,
          experiments: experimentsWithResetTrigger,
        })

        dispatch({
          type: SET_OPTIMIZELY_FEATURE_FLAGS,
          flags: optimizelyFeatureFlags,
        })

        dispatch({
          type: SET_RESOLVED_TRACKING_ID,
          resolvedTrackingId,
        })

        // Log successful re-bucketing to New Relic
        logNewRelic('Optimizely re-bucketed after login', {
          resolvedTrackingId,
          previousTrackingId: previousResolvedId,
          experimentsChanged: changedExperimentKeys.length,
          totalExperiments: Object.keys(optimizelyExperiments).length,
        }, 'info')
      }
    } catch(error) {
      console.error('[Optimizely] Failed to re-bucket experiments:', error)
      // Fail silently - user continues with existing bucketing
      // This ensures login flow isn't blocked by re-bucketing failures

      logNewRelic('Optimizely re-bucketing failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      })
    }
  }
}
